import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { BarChart3, Activity, Pencil, Layers, ArrowRight, TrendingUp } from 'lucide-react';
import { siteConfig } from '@/config/site';

const features = [
    {
        icon: BarChart3,
        title: 'Advanced Charts',
        description: 'Professional-grade charting with candlestick, line, bar, and Heikin-Ashi chart types for comprehensive market visualization.',
    },
    {
        icon: Activity,
        title: '100+ Indicators',
        description: 'Access over 100 technical indicators including RSI, MACD, Bollinger Bands, Fibonacci retracements, and many more.',
    },
    {
        icon: Pencil,
        title: 'Drawing Tools',
        description: 'Full suite of drawing tools — trendlines, channels, pitchforks, and geometric shapes for precise chart annotation.',
    },
    {
        icon: Layers,
        title: 'Multi-Timeframe Analysis',
        description: 'Analyze markets across multiple timeframes simultaneously, from 1-minute scalping to monthly macro views.',
    },
];

export default function TechnicalAnalysis() {
    return (
        <AppLayout title={`Technical Analysis - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <Badge className="mb-6">Trading Tools</Badge>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Technical <span className="text-gradient">Analysis</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                            Professional charting and analysis tools to help you identify opportunities,
                            spot trends, and make data-driven trading decisions.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Chart Placeholder */}
            <Section className="py-20 bg-muted/30 border-y border-border">
                <FadeIn>
                    <Card className="max-w-5xl mx-auto rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                        <CardContent className="p-12 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-primary/10 border border-primary/10 mb-6">
                                <TrendingUp className="w-8 h-8 text-primary" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground mb-4">
                                Charting Platform Coming Soon
                            </h2>
                            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                                Our advanced charting platform will provide real-time price data,
                                customizable technical indicators, and powerful drawing tools — all
                                within a seamless, browser-based interface.
                            </p>
                            <div className="bg-muted rounded-xl border border-border p-16 flex items-center justify-center">
                                <div className="text-muted-foreground text-sm flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5" />
                                    Interactive chart area — coming soon
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </FadeIn>
            </Section>

            {/* Features */}
            <Section className="py-20 bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Analysis"
                        highlight="Features"
                        description="Everything you need for professional-level technical analysis."
                    />
                </FadeIn>
                <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {features.map((feature) => (
                        <motion.div key={feature.title} variants={staggerItem}>
                            <Card className="p-8 rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors h-full">
                                <CardContent className="p-0 flex gap-5">
                                    <div className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 border border-primary/10">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-2">
                                            {feature.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="py-20 bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-2xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Analyze Like a <span className="text-gradient">Pro</span>
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Get access to professional-grade charting and technical analysis tools with {siteConfig.brand.name}.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Started <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </a>
                            <Link to="/tools/technical-analysis">
                                <Button variant="outline" size="lg">Explore Tools</Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
